import { useQuery } from '@tanstack/react-query'
import { ProjectTypes } from 'constants/enums'
import { GOLD_CURRENCY, latestGoldNav } from 'models/productChart'
import type { MoneyDisplay } from 'shared/domain/projectTemplate'
import { stoQueries } from './queries/stoQueries'
import { useStoProject } from './useStoProject'
import { useNavComposition } from './useNavComposition'
import { usePickedCurrency } from './useProductChart'

/**
 * A gold fund (Goldstream) shows its amounts in XAUt, as its charts, unless the hero title's switch
 * picked the fund's own currency. A fund is gold when it publishes XAUt figures: a NAV in XAUt or a
 * NAV composition in XAUt. Amounts convert at today's XAUt price, so until that arrives (in the
 * browser) they stay in the fund's currency; the token price is the published XAUt NAV.
 */
export function useMoneyDisplay(): MoneyDisplay | undefined {
  const { project } = useStoProject()
  const isFund = project?.type === ProjectTypes.FUND
  const compositions = useNavComposition()
  // Same query as the fund's chart and the invest panel: fetched once.
  const { data: navValues } = useQuery(
    stoQueries.nav(isFund ? project?.secondaryAssetId?.trim() || undefined : undefined),
  )
  const picked = usePickedCurrency()
  const goldNav = latestGoldNav(navValues)
  const isGold =
    isFund && (goldNav !== undefined || compositions.some((composition) => composition.currency === GOLD_CURRENCY))
  const currency = project?.tokenCurrency?.trim() || 'USD'
  // As the chart's switch: only the fund's own currency turns gold off; anything else is the default.
  const inGold = isGold && picked !== currency.toLowerCase()
  const { data: perUnit } = useQuery(stoQueries.xautPrice(inGold ? currency : undefined))

  return inGold && perUnit ? { currency: GOLD_CURRENCY, perUnit, tokenPrice: goldNav } : undefined
}
